import {
	createResource,
	type RouterDataContext,
	type ResourceStoreContext,
} from '@atlassian/react-resource-router';
import { fg } from '@atlassian/jira-feature-gating';
// eslint-disable-next-line jira/import-whitelist
import {
	INCIDENT_MANAGEMENT_FEATURE,
	SERVICE_REQUEST_FEATURE,
	DISABLED,
} from '@atlassian/jira-servicedesk-common/src/model/service-project-features.tsx';
import { checkLicense } from '@atlassian/jira-spa-apps-common/src/interceptors/license/index.tsx';
import {
	RESOURCE_TYPE_AI_CONTEXT_PANEL,
	RESOURCE_TYPE_AI_CONTEXT_PANEL_WITH_DEPENDENCIES,
	RESOURCE_TYPE_CONTEXT_PANEL_ISSUE_DETAILS,
} from '../constants.tsx';
import { getDependencyData } from '../utils.tsx';
import type { AIContextServicePanelResourceResponse } from './service/types.tsx';
import { getServiceSuggestionData } from './service/endpoints/index.tsx';
import { getIncidentSuggestionEndpoint } from './ops/endpoints/index.tsx';
import { contextPanelIssueDetailsResource, getIssueDetails } from './issue-details/index.tsx';
import type { IncidentSuggestion } from './ops/common/types.tsx';
import { getFeatureState } from './feature-state/index.tsx';
import type { IssueDetails } from './issue-details/types.tsx';

const SUPPORTED_PRACTICES = new Set<string | null | undefined>([
	SERVICE_REQUEST_FEATURE,
	INCIDENT_MANAGEMENT_FEATURE,
]);

export const aiContextPanelResource = createResource<
	AIContextServicePanelResourceResponse | IncidentSuggestion | null
>({
	type: RESOURCE_TYPE_AI_CONTEXT_PANEL,
	getKey: ({ match }) => {
		const issueKey = match.params.issueKey || '';
		return `aiContextPanelResource-${issueKey}`;
	},
	getData: async (
		routerDataContext: RouterDataContext,
		resourceStoreContext: ResourceStoreContext,
	) => {
		const { skipChecks, issueKey } = routerDataContext.match.params;
		let { issueId, practice, projectId } = routerDataContext.match.params;
		const { cloudId, locale } = resourceStoreContext.tenantContext;

		if (!issueKey) {
			return null;
		}

		if (skipChecks !== '1') {
			// The suggestions panel is available only on service-desk.
			// Hence filtering them out to reduce the number of calls to AGG and features APIs
			if (
				!checkLicense(resourceStoreContext.tenantContext, 'servicedesk') ||
				!fg('jsm_issue_view_ai_context')
			) {
				return null;
			}

			const issueDetails = await getIssueDetails(
				issueKey,
				cloudId,
				fg('context_panel_state_in_issue_details'),
			);
			const isAIEnabled = issueDetails.isAIEnabled;
			issueId = issueDetails.issueId;
			projectId = issueDetails.projectId;
			practice = issueDetails.practice;

			if (!isAIEnabled || !SUPPORTED_PRACTICES.has(practice)) {
				return null;
			}

			let isAIContextPanelDisabled;

			if (fg('context_panel_state_in_issue_details')) {
				isAIContextPanelDisabled = !issueDetails.isAiContextFeatureEnabled;
			} else {
				const featureState = await getFeatureState(routerDataContext, resourceStoreContext);
				isAIContextPanelDisabled = featureState?.['jsm-ai-context']?.status === DISABLED;
			}

			if (isAIContextPanelDisabled) {
				return null;
			}
		}

		if (!issueId) {
			throw new Error('AI context service panel resource cannot be loaded without an issueId');
		}

		if (practice === SERVICE_REQUEST_FEATURE) {
			return getServiceSuggestionData(issueId, cloudId);
		}
		if (practice === INCIDENT_MANAGEMENT_FEATURE && fg('jsm_issue_view_ai_context_ops')) {
			if (!projectId) {
				throw new Error('AI context ops panel resource cannot be loaded without a projectId');
			}

			return getIncidentSuggestionEndpoint({
				cloudId,
				issueId,
				projectId,
				locale,
			});
		}
		return null;
	},
	maxAge: 0,
	isBrowserOnly: true,
});

// eslint-disable-next-line jira/router/no-daisy-chaining-resources
export const aiContextPanelResourceWithDependencies = createResource<
	AIContextServicePanelResourceResponse | IncidentSuggestion | null
>({
	type: RESOURCE_TYPE_AI_CONTEXT_PANEL_WITH_DEPENDENCIES,
	getKey: ({ match }) => {
		const issueKey = match.params.issueKey || '';
		return `aiContextPanelResourceWithDependencies-${issueKey}`;
	},
	getData: async (
		routerDataContext: RouterDataContext,
		resourceStoreContext: ResourceStoreContext,
	) => {
		const { skipChecks, issueKey } = routerDataContext.match.params;
		let { issueId, practice, projectId } = routerDataContext.match.params;
		const { cloudId, locale } = resourceStoreContext.tenantContext;

		if (!issueKey) {
			return null;
		}

		if (skipChecks !== '1') {
			if (
				!checkLicense(resourceStoreContext.tenantContext, 'servicedesk') ||
				!fg('jsm_issue_view_ai_context')
			) {
				return null;
			}

			let issueDetails = getDependencyData<IssueDetails | null>(
				routerDataContext.dependencies,
				RESOURCE_TYPE_CONTEXT_PANEL_ISSUE_DETAILS,
			);
			if (issueDetails instanceof Promise) {
				issueDetails = await issueDetails;
			}

			if (!issueDetails) {
				return null;
			}

			const { isAIEnabled, isAiContextFeatureEnabled } = issueDetails;
			issueId = issueDetails.issueId;
			projectId = issueDetails.projectId;
			practice = issueDetails.practice;

			if (!isAIEnabled || !SUPPORTED_PRACTICES.has(practice) || !isAiContextFeatureEnabled) {
				return null;
			}
		}

		if (!issueId) {
			throw new Error('AI context service panel resource cannot be loaded without an issueId');
		}

		if (practice === SERVICE_REQUEST_FEATURE) {
			return getServiceSuggestionData(issueId, cloudId);
		}
		if (practice === INCIDENT_MANAGEMENT_FEATURE && fg('jsm_issue_view_ai_context_ops')) {
			if (!projectId) {
				throw new Error('AI context ops panel resource cannot be loaded without a projectId');
			}

			return getIncidentSuggestionEndpoint({
				cloudId,
				issueId,
				projectId,
				locale,
			});
		}
		return null;
	},
	maxAge: 0,
	depends: [RESOURCE_TYPE_CONTEXT_PANEL_ISSUE_DETAILS],
	isBrowserOnly: true,
});

export const getAIContextPanelResources = () => {
	return fg('ssr_issue_details_for_suggestions_panel')
		? [contextPanelIssueDetailsResource, aiContextPanelResourceWithDependencies]
		: [aiContextPanelResource];
};
